import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LoadingBoxComponent } from '../../../../../shared/components/loading-box/loading-box.component';
import { ContasPagarService } from '../../../../../shared/services/apis/financas/contas-pagar.service';
import { ContasPagarGetAllRequest } from '../../../../../shared/services/requests/financas/contas-pagar-get-all.request';
import { ContasPagarGridItemStruct } from '../../../../../shared/services/structs/financas/contas-pagar-grid-item.struct';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { PessoaLookupService } from '../../../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ListPessoaIdsRequest } from '../../../../../shared/services/requests/pessoa/list-pessoa-ids-request';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { DatePipe } from '@angular/common';

export interface ImpressaoContasPagarDialogData {
    filtros: ContasPagarGetAllRequest;
}

@Component({
    selector: 'app-impressao-contas-pagar-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        LoadingBoxComponent,
        AbntPipe,
        DatePipe
    ],
    templateUrl: './impressao-contas-pagar-dialog.component.html',
    styleUrls: ['./impressao-contas-pagar-dialog.component.css']
})
export class ImpressaoContasPagarDialogComponent implements OnInit {

    public isLoading: boolean = false;
    public relatorioContas: ContasPagarGridItemStruct[] = [];
    public displayedColumns: string[] = ['fornecedor', 'historico', 'numeroDocumento', 'valor', 'pago', 'aPagar', 'vencimento', 'situacao'];

    // Totais
    public totalVencidos: number = 0;
    public totalVencemHoje: number = 0;
    public totalAVencer: number = 0;
    public totalPagos: number = 0;
    public totalPeriodo: number = 0;
    public quantidadeLancamentos: number = 0;

    constructor(
        public dialogRef: MatDialogRef<ImpressaoContasPagarDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ImpressaoContasPagarDialogData,
        private contasPagarService: ContasPagarService,
        private alertService: AlertService,
        private pessoaLookupService: PessoaLookupService
    ) { }

    ngOnInit(): void {
        this.buscarRelatorio();
    }

    private buscarRelatorio(): void {
        this.isLoading = true;
        const request = new ContasPagarGetAllRequest();
        Object.assign(request, this.data.filtros);

        this.contasPagarService.getPrint(request).subscribe({
            next: (res) => {
                if (!res.isError) {
                    this.relatorioContas = res.itens || [];
                    this.totalVencidos = res.totalVencidos;
                    this.totalVencemHoje = res.totalVencemHoje;
                    this.totalAVencer = res.totalAVencer;
                    this.totalPagos = res.totalPagos;
                    this.totalPeriodo = res.totalPeriodo;
                    this.quantidadeLancamentos = res.quantidadeLancamentos;
                    this.carregarNomesFornecedores();
                } else {
                    this.isLoading = false;
                    this.alertService.show('Erro', res.errorDescription, AlertType.error);
                    this.fechar();
                }
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Erro ao gerar relatório de contas a pagar', AlertType.error);
                this.fechar();
            }
        });
    }

    // A API devolve "fornecedor" como a descrição livre do documento, não o nome do fornecedor
    // (mesma causa-raiz da busca por fornecedor não funcionar) — resolve o nome de verdade aqui,
    // igual já é feito na listagem (contas-pagar-lista.component.ts).
    private carregarNomesFornecedores(): void {
        const ids = this.relatorioContas
            .map(i => i.idFornecedor)
            .filter((id, index, self) => id != null && self.indexOf(id) === index) as number[];

        if (ids.length === 0) {
            this.isLoading = false;
            return;
        }

        const req = new ListPessoaIdsRequest();
        req.listIdPessoa = ids;

        this.pessoaLookupService.getListPessoaIds(req).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.isError) {
                    return;
                }
                const dictPessoas = new Map(res.listaPessoa.map(p => [p.idPessoa, p.nome || p.nomeFantasia]));
                this.relatorioContas.forEach(item => {
                    if (item.idFornecedor && dictPessoas.has(item.idFornecedor)) {
                        item.fornecedor = dictPessoas.get(item.idFornecedor)!;
                    }
                });
            },
            error: () => {
                this.isLoading = false;
            }
        });
    }

    public imprimir(): void {
        const conteudoParaImprimir = document.querySelector('.modal-print-container');

        if (conteudoParaImprimir) {
            const janelaImpressao = window.open('', '', 'height=600,width=1000');

            if (janelaImpressao) {
                const styles = Array.from(document.styleSheets)
                    .map(styleSheet => {
                        try {
                            return Array.from(styleSheet.cssRules)
                                .map(rule => rule.cssText)
                                .join('');
                        } catch (e) {
                            return '';
                        }
                    })
                    .join('');

                janelaImpressao.document.write(`
          <html>
            <head>
              <title>Imprimir Contas a Pagar</title>
              <base href="/">
              <style>
                ${styles}
                /* Print Specific Overrides */
                html, body {
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    margin: 20px;
                    font-family: Roboto, "Helvetica Neue", sans-serif;
                    color: #000;
                    background: #fff;
                }
                .no-print { display: none !important; }
                .modal-print-container {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                    min-height: 0 !important;
                    overflow: visible !important;
                    box-shadow: none !important;
                    margin: 0 !important;
                    padding: 0 !important;
                }
                
                /* Force MatTable styles for print */
                .mat-mdc-table { background: white !important; width: 100% !important; }
                .mat-mdc-header-cell { color: black !important; font-weight: bold !important; border-bottom: 2px solid black !important; font-size: 11px !important; }
                .mat-mdc-cell { color: black !important; border-bottom: 1px solid #ddd !important; font-size: 11px !important; }
                
                .title-header { text-align: center; margin-bottom: 20px; }
                h2 { font-size: 16px; margin: 0; }
                
                .print-footer-resumo {
                    display: flex;
                    justify-content: space-around;
                    padding: 12px 0;
                    border-top: 2px solid #333;
                    margin-top: 16px;
                    font-size: 12px;
                }
                .print-footer-resumo .item { text-align: center; }
                .print-footer-resumo .item .label { font-weight: bold; font-size: 10px; text-transform: uppercase; color: #555; }
                .print-footer-resumo .item .value { font-size: 13px; font-weight: 600; margin-top: 2px; }
              </style>
            </head>
            <body>
              ${conteudoParaImprimir.innerHTML}
            </body>
          </html>
        `);

                janelaImpressao.document.close();
                janelaImpressao.focus();

                setTimeout(() => {
                    janelaImpressao.print();
                    janelaImpressao.close();
                }, 800);
            }
        }
    }

    public fechar(): void {
        this.dialogRef.close();
    }
}
